import React, {useContext, useEffect, useMemo, useState} from 'react';
import chatStyles from './LiveChatModule.module.css';
import styles from './Leaderboard.module.css';
import { PRISMAX_BACKEND_Control_URL } from '../../configs/config';

import leaderboardGoldSvg from '../../static_files/leaderboard-gold.svg';
import leaderboardSilverSvg from '../../static_files/leaderboard-silver.svg';
import leaderboardBronzeSvg from '../../static_files/leaderboard-bronze.svg';
import badgeInnovatorSvg from '../../static_files/badge_innovator.svg';
import badgeAmplifierSvg from '../../static_files/badge_amplifier.svg';
import badgeExplorerSvg from '../../static_files/badge_explorer.svg';
import leaderboardFallbackIconSvg from '../../static_files/leaderboard-fallback-icon.svg';

import { shortenAddress } from '../../utils/utils';
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import TooltipLeaderboard from '../Tooltip/TooltipLeaderboard';

const NARROW_BREAKPOINT = 433;
const NARROW_MIN_SCROLL_WIDTH = 406;

function normalizeLeaderboardEntry(entry, idx = 0) {
  if (!entry || typeof entry !== 'object') return null;

  const rankRaw =
    entry.rank ??
    entry.position ??
    entry.ranking ??
    entry.place ??
    entry.index;
  const fallbackRank = idx + 1;
  const rank =
    rankRaw != null && !Number.isNaN(Number(rankRaw)) ? Number(rankRaw) : fallbackRank;

  const userRaw =
    entry.user ??
    entry.username ??
    entry.name ??
    entry.displayName ??
    entry.display_name ??
    (typeof entry.user === 'object'
      ? entry.user.username || entry.user.name || entry.user.displayName
      : undefined) ??
    entry.email ??
    entry.wallet_address ??
    entry.id ??
    '—';

  const displayUser = (() => {
    if (entry.email) return shortenAddress(entry.email);
    if (entry.wallet_address) return shortenAddress(entry.wallet_address);
    return String(userRaw);
  })();

  const hoursCandidate =
    entry.hours ??
    entry.time ??
    entry.total_hours ??
    entry.hours_total ??
    entry.controlled_hours ??
    (entry.minutes != null ? Number(entry.minutes) / 60 : undefined) ??
    (entry.total_minutes != null ? Number(entry.total_minutes) / 60 : undefined) ??
    (entry.seconds != null ? Number(entry.seconds) / 3600 : undefined) ??
    (entry.total_seconds != null ? Number(entry.total_seconds) / 3600 : undefined);

  const hours =
    hoursCandidate == null || Number.isNaN(Number(hoursCandidate))
      ? null
      : Number(hoursCandidate);

  const prismaRaw =
    entry.prisma ??
    entry.points ??
    entry.score ??
    entry.prisma_points ??
    entry.tele_op_points ??
    entry.teleop_points ??
    entry.total_points ??
    0;
  const prisma = Number.isNaN(Number(prismaRaw)) ? 0 : Number(prismaRaw);

  const resolvedUserId =
    entry.user_id ??
    entry.userId ??
    entry.userid ??
    entry.uid ??
    entry.id ??
    (typeof entry.user === 'object'
      ? entry.user.user_id || entry.user.userId || entry.user.userid || entry.user.id
      : undefined);

  return {
    ...entry,
    rank,
    displayUser,
    hours,
    prisma,
    normalizedUser: String(userRaw ?? '—'),
    resolvedUserId: resolvedUserId != null ? String(resolvedUserId) : null,
  };
}

export default function Leaderboard({ leaderboardChain = 'default' }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [selfEntry, setSelfEntry] = useState(null);
  const { userId } = useContext(SwapMainAppContext);
  const [isNarrowScreen, setIsNarrowScreen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < NARROW_BREAKPOINT;
  });

  const endpoint = useMemo(() => {
    const path =
      leaderboardChain === 'monad'
        ? '/tele_op/monad_leaderboard'
        : '/tele_op/leaderboard';
    const base = `${PRISMAX_BACKEND_Control_URL}${path}`;
    if (userId === undefined || userId === null || userId === '') return base;
    return `${base}?userId=${encodeURIComponent(String(userId))}`;
  }, [userId, leaderboardChain]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setStatus('loading');
        setError(null);
        setSelfEntry(null);
        const res = await fetch(endpoint, {
          method: 'GET',
          signal: ctrl.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const arr = (() => {
          if (Array.isArray(data)) return data;
          if (Array.isArray(data?.data)) return data.data;
          if (Array.isArray(data?.leaderboard)) return data.leaderboard;
          if (Array.isArray(data?.result)) return data.result;
          if (Array.isArray(data?.items)) return data.items;
          if (Array.isArray(data?.rows)) return data.rows;
          if (Array.isArray(data?.records)) return data.records;
          if (Array.isArray(data?.list)) return data.list;
          if (data && typeof data === 'object') {
            const v = Object.values(data).find(
              (x) => Array.isArray(x) && (x.length === 0 || typeof x[0] === 'object')
            );
            if (Array.isArray(v)) return v;
            if (data.leaderboard && typeof data.leaderboard === 'object') {
              const inner = Object.values(data.leaderboard).find(Array.isArray);
              if (Array.isArray(inner)) return inner;
            }
          }
          return [];
        })();

        const normalized = arr
          .map((it, idx) => normalizeLeaderboardEntry(it, idx))
          .filter(Boolean);

        const selfCandidate = data?.self ?? data?.user ?? data?.me ?? null;
        const normalizedSelf = normalizeLeaderboardEntry(selfCandidate, normalized.length);

        setItems(normalized);
        setSelfEntry(
          normalizedSelf && normalizedSelf.resolvedUserId ? normalizedSelf : null
        );
        setStatus('success');
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(e);
          setStatus('error');
        }
      }
    })();
    return () => ctrl.abort();
  }, [endpoint]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsNarrowScreen(window.innerWidth < NARROW_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const selfId = userId != null ? String(userId) : "";
  const hasSelfInTop =
    selfId &&
    items.some((item) => item && item.resolvedUserId === selfId);
  const appendedSelf =
    selfId &&
    selfEntry &&
    selfEntry.resolvedUserId === selfId &&
    Number(selfEntry.rank ?? 0) > 50 &&
    !hasSelfInTop
      ? selfEntry
      : null;
  const isMonadChain = leaderboardChain === 'monad';

  const renderRow = (item, idx, keyOverride) => {
    if (!item) return null;
    const parsedRank = Number(item.rank);
    const fallbackRank = idx + 1;
    const rank = Number.isFinite(parsedRank) && parsedRank > 0 ? parsedRank : fallbackRank;
    
    const isTop4 = rank === 4;
    const isSelf =
      selfId &&
      item.resolvedUserId &&
      item.resolvedUserId === selfId;

    const rowClass = [
      styles.row,
      isTop4 && styles.rowTop4,
      isSelf && styles.rowHighlighted,
    ]
      .filter(Boolean)
      .join(' ');

    const nameToShow = String(item.displayUser ?? item.user ?? item.normalizedUser ?? '—');
    const tooltipText = String(
      item.email || item.wallet_address || item.user || item.normalizedUser || ''
    );
    const rewardValue = isMonadChain ? getMonadRewardForRank(rank) : null;

    const key =
      keyOverride ??
      (item.resolvedUserId ? `${item.resolvedUserId}-${rank}` : `${rank}-${idx}`);

    const leaderboardRankToIcon = new Map([
      [1, leaderboardGoldSvg],
      [2, leaderboardSilverSvg],
      [3, leaderboardBronzeSvg]
    ]);

    const userClassToRenderContent = new Map([
      [
        'Innovator Member',
        { badge: badgeInnovatorSvg, tooltipColor: '#C765B0', text: 'Innovator Member' }
      ],
      [
        'Amplifier Member',
        { badge: badgeAmplifierSvg, tooltipColor: '#DE8561', text: 'Amplifier Member' }
      ],
      [
        'Explorer Member',
        { badge: badgeExplorerSvg, tooltipColor: '#6AB2D6', text: 'Explorer Member' }
      ]
    ]);
    const userClassRenderContent = userClassToRenderContent.get(item.user_class);

    return (
      <>
      <div className={rowClass} key={key}>
        <div className={styles.cellRank}>
          {rank <= 3 ? (
            <img
              src={leaderboardRankToIcon.get(rank)}
              alt="Badges for top three users"
              className={styles.trophyImg}
            />
          ) : (
            <span className={styles.rankNumber}>{rank}</span>
          )}
        </div>

        <div className={styles.cellUser}>
          <TooltipLeaderboard
            text={userClassRenderContent?.text}
            fontColor={userClassRenderContent?.tooltipColor}
            backgroundColor="#FFFFFF"
          >
             <span className={styles.avatarWrap}>
                <img
                    src={userClassRenderContent?.badge ?? leaderboardFallbackIconSvg}
                    alt="User membership account icon"
                    className={styles.avatarImg} 
                  />           
              </span>
          </TooltipLeaderboard>
         

          <span className={styles.username} title={tooltipText}>
            {nameToShow}
          </span>
        </div>

        <div className={styles.cellHours}>
          {isMonadChain ? formatReward(rewardValue) : formatHours(item.hours)}
        </div>

        <div className={styles.cellPrisma}>
          <span className={styles.prismaValue}>
            {formatPrisma(item.prisma)}
          </span>
        </div>
      </div>
      </>
    );
  };

  const content = (
    <>
      <div
        className={styles.tableHeader}
        style={isNarrowScreen ? { minWidth: NARROW_MIN_SCROLL_WIDTH } : undefined}
      >
        <div className={styles.colRank}>Rank</div>
        <div className={styles.colUser}>User</div>
        <div className={styles.colHours}>{isMonadChain ? 'Rewards' : 'Hours'}</div>
        <div className={styles.colPrisma}>Prisma</div>
      </div>

      <div
        className={`${chatStyles.messagesContainer} ${styles.list}`}
        style={isNarrowScreen ? { minWidth: NARROW_MIN_SCROLL_WIDTH } : undefined}
      >
        <div className={isNarrowScreen ? styles.scrollInner : undefined}>
          {status === 'loading' && (
            <div className={styles.skeletonWrap}>
              {Array.from({ length: 9 }).map((_, i) => (
                <div className={styles.skeletonRow} key={i} />
              ))}
            </div>
          )}

          {status === 'error' && (
            <div className={chatStyles.emptyState}>
              Failed to load{error ? `: ${error.message}` : ''}.
            </div>
          )}

          {status === 'success' && items.length === 0 && (
            <div className={chatStyles.emptyState}>No entries yet.</div>
          )}

          {status === 'success' &&
            items.map((item, idx) => renderRow(item, idx))}

          {status === 'success' && appendedSelf &&
            renderRow(appendedSelf, items.length, 'self-extra')}
        </div>
      </div>
    </>
  );

  return isNarrowScreen ? (
    <div className={styles.horizontalScrollWrapper}>{content}</div>
  ) : (
    content
  );
}

function formatHours(v) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '–';
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function getMonadRewardForRank(rank) {
  if (!Number.isFinite(rank)) return null;
  if (rank === 1) return 2500;
  if (rank === 2) return 1250;
  if (rank === 3) return 650;
  if (rank === 4 || rank === 5) return 300;
  if (rank >= 6 && rank <= 10) return 200;
  if (rank >= 11 && rank <= 50) return 100;
  return null;
}

function formatReward(v) {
  if (v == null) return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatPrisma(v) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
